import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, CheckCircle2, DollarSign, UserPlus, PhoneMissed, MessageSquare, Receipt, Package,
} from "lucide-react";
import { Panel, money, timeAgo } from "./singleUtils";

const ICONS = {
  job_completed: { icon: CheckCircle2, tone: "text-emerald-600 dark:text-emerald-400" },
  payment: { icon: DollarSign, tone: "text-emerald-600 dark:text-emerald-400" },
  new_customer: { icon: UserPlus, tone: "text-indigo-600 dark:text-indigo-400" },
  missed_call: { icon: PhoneMissed, tone: "text-rose-600 dark:text-rose-500" },
  sms: { icon: MessageSquare, tone: "text-indigo-600 dark:text-indigo-400" },
  expense: { icon: Receipt, tone: "text-rose-600 dark:text-rose-500" },
};

const line = (a, financialAccess) => {
  switch (a.type) {
    case "job_completed": return `${a.title || "Job"} completed${a.who ? ` — ${a.who}` : ""}`;
    case "payment": return `${financialAccess && a.amount != null ? money(a.amount) + " " : ""}payment received${a.title ? ` · ${a.title}` : ""}`;
    case "new_customer": return `New customer — ${a.who || ""}`;
    case "missed_call": return `Missed call — ${a.who || "Unknown"}`;
    case "sms": return `SMS from ${a.who || "Unknown"}${a.text ? `: "${a.text}"` : ""}`;
    case "expense": return `${financialAccess && a.amount != null ? money(a.amount) + " " : ""}expense added · ${a.who || "Other"}${a.title ? ` — ${a.title}` : ""}`;
    default: return a.title || a.type;
  }
};

export const InventoryCard = ({ inventory }) => {
  const navigate = useNavigate();
  const inv = inventory || {};
  if (inv.error) return null;
  const alerts = (inv.low_stock || 0) + (inv.out_of_stock || 0);
  return (
    <button onClick={() => navigate("/dashboard/inventory")}
      className={`w-full rounded-md border px-3.5 py-2.5 text-left transition-colors flex items-center justify-between gap-2 ${
        alerts > 0 ? "border-amber-500/40 bg-amber-500/[0.05] hover:bg-amber-500/[0.09]" : "border-border bg-card hover:border-amber-500/30"
      }`}
      data-testid="inventory-card">
      <span className="flex items-center gap-2 min-w-0">
        <Package className={`w-4 h-4 shrink-0 ${alerts > 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`} />
        <span className="text-xs font-medium text-foreground truncate">
          {alerts > 0
            ? `${inv.low_stock || 0} low stock · ${inv.out_of_stock || 0} out of stock`
            : "Inventory OK — no stock alerts"}
        </span>
      </span>
      <span className="text-[10px] font-mono text-muted-foreground shrink-0">{inv.total_items ?? 0} items</span>
    </button>
  );
};

const ActivityLog = ({ activity, financialAccess }) => {
  const list = activity || [];
  return (
    <Panel title="Recent Activity" icon={Activity} testid="activity-log">
      {list.length === 0 ? (
        <p className="text-[11px] text-muted-foreground py-2 text-center" data-testid="activity-empty">No activity in this period yet</p>
      ) : (
        <div className="divide-y divide-border/40 max-h-[320px] overflow-y-auto">
          {list.slice(0, 12).map((a, i) => {
            const cfg = ICONS[a.type] || { icon: Activity, tone: "text-muted-foreground" };
            const Icon = cfg.icon;
            return (
              <div key={i} className="flex items-start gap-2 py-1.5" data-testid={`activity-item-${i}`}>
                <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${cfg.tone}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-foreground leading-snug break-words">{line(a, financialAccess)}</p>
                  <p className="text-[10px] text-muted-foreground font-mono mt-px">{timeAgo(a.at)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
};

export default ActivityLog;
